// src/utils/mapUx.jsx

/**
 * Disable common map interactions (dragging, touch zoom, double-click zoom).
 * Useful while hovering UI overlays on top of the map; wheel zoom is blocked by MapUiSlot.
 *
 * @param {import('leaflet').Map | null | undefined} map - Leaflet map instance.
 */
export function onMouseEnter(map) {
  if (!map) return
  map.dragging?.disable()
  map.touchZoom?.disable()
  map.doubleClickZoom?.disable()
}

/**
 * Re-enable common map interactions (dragging, touch zoom, double-click zoom).
 * Call this when the pointer leaves your overlay.
 *
 * @param {import('leaflet').Map | null | undefined} map - Leaflet map instance.
 */
export function onMouseLeave(map) {
  if (!map) return
  map.dragging?.enable()
  map.touchZoom?.enable()
  map.doubleClickZoom?.enable()
}

/**
 * Ensure a custom pane exists and (optionally) set its z-index.
 * Returns the pane DOM element so you can tweak styles if needed.
 *
 * @param {import('leaflet').Map} map - Leaflet map instance.
 * @param {string} paneName - Name of the pane (e.g. "vesselIconPane").
 * @param {number} [zIndex] - Optional z-index to apply (higher draws on top).
 * @returns {HTMLElement} The pane DOM element.
 */
export function ensurePane(mapOrLayer, paneName, zIndex) {
  const map =
    mapOrLayer && typeof mapOrLayer.getPane === "function" && typeof mapOrLayer.createPane === "function"
      ? mapOrLayer
      : mapOrLayer?._map || null

  if (!map) {
    console.error("ensurePane: no Leaflet Map provided. Pass a real L.Map or a layer that is added to a map.")
    return null
  }
  if (!map.getPane(paneName)) map.createPane(paneName)
  const pane = map.getPane(paneName)
  if (zIndex != null) pane.style.zIndex = String(zIndex)
  return pane
}
